import { useRef } from "react";
import { Loader2, Plus, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface GlobalKeywordsAddFormProps {
  newKeyword: string;
  onNewKeywordChange: (value: string) => void;
  onAddSingle: () => void;
  isAddingSingle: boolean;
  bulkText: string;
  onBulkTextChange: (value: string) => void;
  onBulkAdd: () => void;
  isAddingBulk: boolean;
  onFileUpload: (file: File) => void;
}

// AUT-41: the top-right card of GlobalProhibitedKeywordsTab -- Add Single/Bulk/File Upload.
export function GlobalKeywordsAddForm({
  newKeyword,
  onNewKeywordChange,
  onAddSingle,
  isAddingSingle,
  bulkText,
  onBulkTextChange,
  onBulkAdd,
  isAddingBulk,
  onFileUpload,
}: GlobalKeywordsAddFormProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onFileUpload(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <Card className="p-6 space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-foreground">Add Global Prohibited Keywords</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Keywords are normalized to lowercase. Duplicates are skipped automatically.
        </p>
      </div>

      <Tabs defaultValue="single" className="w-full">
        <TabsList className="w-full">
          <TabsTrigger value="single" className="flex-1">Add Single</TabsTrigger>
          <TabsTrigger value="bulk" className="flex-1">Bulk Add</TabsTrigger>
          <TabsTrigger value="upload" className="flex-1">File Upload</TabsTrigger>
        </TabsList>

        <TabsContent value="single" className="mt-4">
          <div className="flex gap-2">
            <Input
              value={newKeyword}
              onChange={(e) => onNewKeywordChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onAddSingle();
                }
              }}
              placeholder="Add a prohibited keyword..."
              className="flex-1"
            />
            <Button onClick={onAddSingle} size="sm" disabled={isAddingSingle}>
              {isAddingSingle ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-1" />
              )}
              Add
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="bulk" className="mt-4 space-y-2">
          <Textarea
            value={bulkText}
            onChange={(e) => onBulkTextChange(e.target.value)}
            placeholder="Paste keywords separated by commas or new lines..."
            rows={5}
          />
          <Button onClick={onBulkAdd} size="sm" className="w-full" disabled={isAddingBulk}>
            {isAddingBulk ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-1" />
            )}
            Add All
          </Button>
        </TabsContent>

        <TabsContent value="upload" className="mt-4">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.txt"
            onChange={handleFileInputChange}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-full flex flex-col items-center gap-2 py-8 rounded-lg border-2 border-dashed border-border hover:border-primary/50 hover:bg-muted/30 transition-colors cursor-pointer"
          >
            <Upload className="h-8 w-8 text-muted-foreground" />
            <div className="text-sm text-muted-foreground">
              Click to upload <span className="font-medium text-foreground">CSV or TXT</span>
            </div>
            <div className="text-xs text-muted-foreground">
              One keyword per line, or comma-separated
            </div>
          </button>
        </TabsContent>
      </Tabs>
    </Card>
  );
}
